import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

export const Container = ({ className = "", children }) => (
  <div className={cn("mx-auto w-full max-w-[1320px] px-6 sm:px-8 lg:px-12", className)}>
    {children}
  </div>
);

export const Eyebrow = ({ children, className = "" }) => (
  <span
    className={cn(
      "inline-flex items-center gap-2 font-mono text-xs font-medium uppercase tracking-[0.28em] text-[var(--primary)]",
      className
    )}
  >
    <span className="h-px w-6 bg-[var(--primary)]/50" />
    {children}
  </span>
);

const base =
  "inline-flex items-center justify-center gap-2 rounded-full font-medium tracking-wide transition-[transform,background-color,color,border-color] duration-300 will-change-transform active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]";

const sizes = {
  md: "px-6 py-3 text-sm",
  lg: "px-8 py-4 text-sm sm:text-base",
};

const variants = {
  primary:
    "bg-[var(--primary)] text-[var(--on-primary)] hover:bg-[var(--primary-hover)] hover:-translate-y-0.5 shadow-[0_10px_30px_-12px_var(--primary)]",
  outline:
    "border border-[var(--fg)]/20 text-[var(--fg)] hover:border-[var(--primary)] hover:text-[var(--primary)] hover:-translate-y-0.5",
  ghost: "text-[var(--fg)] hover:text-[var(--primary)]",
  light:
    "bg-[var(--surface)] text-[var(--fg)] border border-[var(--border)] hover:border-[var(--primary)] hover:-translate-y-0.5",
};

export const Button = React.forwardRef(
  ({ variant = "primary", size = "md", as, to, href, className = "", children, ...props }, ref) => {
    const cls = cn(base, sizes[size], variants[variant], className);
    if (to) {
      return (
        <Link ref={ref} to={to} className={cls} {...props}>
          {children}
        </Link>
      );
    }
    if (href) {
      return (
        <a ref={ref} href={href} className={cls} {...props}>
          {children}
        </a>
      );
    }
    return (
      <button ref={ref} className={cls} {...props}>
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";

export const SectionHeading = ({ eyebrow, title, intro, align = "left", className = "" }) => (
  <div
    className={cn(
      "max-w-3xl",
      align === "center" && "mx-auto text-center",
      className
    )}
  >
    {eyebrow && <Eyebrow className={align === "center" ? "justify-center" : ""}>{eyebrow}</Eyebrow>}
    <h2 className="mt-5 text-4xl font-light leading-[1.05] tracking-tight text-[var(--fg)] sm:text-5xl md:text-6xl text-balance">
      {title}
    </h2>
    {intro && (
      <p className="mt-6 text-base font-light leading-relaxed text-[var(--muted-fg)] sm:text-lg">
        {intro}
      </p>
    )}
  </div>
);
